"use client";

import cn from "@utils/cn";
import { getLink } from "@utils/linkUtils";
import { useTranslations } from "next-intl";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useContext, useEffect, useMemo } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { ModalsContext } from "@/context/ModalsContext";
import { entryPointCtaClick } from "@/dataLayer/dataLayer";
import { isAddToCalendarLink } from "@/utils/addToCalendarLink";

import AddToCalendarButton from "../AddToCalendar/AddToCalendarButton";
import AddToFavorites from "../AddToFavorites/AddToFavorites";
import Icon from "../ui/Icon/Icon";
import ModalContent from "../ui/Modal/ModalContent";
import { StickyBarEditorialProps } from "./types";

export default function StickyBarEditorial({ entry, ...props }: StickyBarEditorialProps) {
  const t = useTranslations();
  const locale = useLocale();
  const { title, subtitle, ctaPrimary, ctaSecondary, addToFavorites, logo, eventDate, eventLocation, description } = entry;
  const calendarEvent = { title, description, location: eventLocation, startDate: eventDate as string };
  const { setBookVisitModalOpen, setGetInTouchModalOpen } = useContext(ModalsContext);
  const { additionalInfo, setAdditionalInfo } = useContext(ModalsContext);
  const { projectPrice, projectTypes, unitBedroom, projectName, projectCity, projectStatus } = props;

  const bedroomsValue = useMemo(() => {
    if (unitBedroom) {
      // BUG 94482
      // if (unitBedroom.min != unitBedroom.max) {
      if (unitBedroom.min != unitBedroom.max && unitBedroom.max != 0) {
        return `${unitBedroom.min}-${unitBedroom.max}`;
      } else {
        return `${unitBedroom.min}`;
      }
    }
    return "";
  }, [t, unitBedroom]);

  useEffect(() => {
    setAdditionalInfo({
      property_category: projectTypes,
      property_indicative_price: Math.round(projectPrice / 100) || 0,
      project_name: projectName,
      project_city: projectCity,
      project_type: projectTypes,
      bedrooms_number: bedroomsValue as string,
      project_status: projectStatus,
    });
  }, [bedroomsValue, projectCity, projectName, projectPrice, projectTypes, setAdditionalInfo]);
  console.log("====================ctaPrimary", ctaPrimary);
  return (
    <section className="sticky z-10 left-0 right-0 top-auto flex items-center mx-auto rounded-full bottom-m  gap-xs p-xxs box-material-tertiary backdrop-blur-md w-22\/24 !px-xxs md:bottom-l md:pr-xxs md:rtl:pl-xxs mb-m md:!pl-l">
      {logo && <Image src={logo.url} alt={logo.title || ""} width="70" height="40" className="w-[70px] h-[40px] mx-l hidden md:block" />}
      <div className="hidden md:block pl-m rtl:mr-l rtl:pr-m font-primary uppercase">
        <p className="text-s font-medium mb-xxs">{title}</p>
        <p className="text-xs">{subtitle}</p>
      </div>
      <div className="flex ml-auto rtl:ml-xxs rtl:mr-auto gap-xs w-full md:w-auto text-center items-center ">
        {ctaSecondary &&
          (isAddToCalendarLink(ctaSecondary.link.url) ? (
            <AddToCalendarButton
              label={ctaSecondary.label}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
              theme={ButtonTheme.BLACK}
              panelPosition="top"
              buttonClassName="btn btn-medium btn-secondary-black w-full md:w-fit flex align-center justify-center"
              icon={ctaSecondary.link.icon && <Icon className="stroke-inherit" name={ctaSecondary.link.icon} size="20" />}
              event={calendarEvent}
              onSelect={() => entryPointCtaClick({ cta_name: ctaSecondary.label, ...additionalInfo })}
            />
          ) : (
            <Button
              href={ctaSecondary.link.url}
              size={ButtonSize.MEDIUM}
              variant={ButtonVariant.SECONDARY}
              theme={ButtonTheme.BLACK}
              customClass="w-full md:w-fit flex align-center justify-center"
              onClick={() => {
                entryPointCtaClick({ cta_name: ctaSecondary.label, ...additionalInfo });
                setBookVisitModalOpen(true);
              }}
            >
              <span className="flex items-center justify-center gap-xs ">
                {ctaSecondary.link.icon && <Icon className="stroke-inherit" name={ctaSecondary.link.icon} size="20" />}

                {ctaSecondary.label}
              </span>
            </Button>
          ))}
        {isAddToCalendarLink(ctaPrimary.link.url) ? (
          <AddToCalendarButton
            label={ctaPrimary.label}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.BLACK}
            panelPosition="top"
            buttonClassName="btn btn-medium btn-secondary-black w-full md:w-fit flex align-center justify-center"
            icon={ctaPrimary.link.icon && <Icon className="stroke-inherit" name={ctaPrimary.link.icon} size="20" />}
            event={calendarEvent}
            onSelect={() => entryPointCtaClick({ cta_name: ctaPrimary.label, ...additionalInfo })}
          />
        ) : (
          <Button
            href={getLink(ctaPrimary, locale)}
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.BLACK}
            customClass="w-full md:w-fit flex align-center justify-center"
            onClick={() => {
              const AULComponent = document.body.querySelector("#AUL-component");
              if (AULComponent) {
                const rect = AULComponent.getBoundingClientRect();
                window.scrollTo({
                  top: rect.top + window.scrollY,
                  behavior: "smooth",
                });
              }
              if (ctaPrimary.label?.trim().toLowerCase() === t("get-in-touch") || "get in touch") {
                setGetInTouchModalOpen(true);
              }
            }}
          >
            <span className="items-center justify-center gap-xs inline-flex">
              {ctaPrimary.link.icon && <Icon className="stroke-inherit" name={ctaPrimary.link.icon} size="20" />}
              {ctaPrimary.label}
            </span>
          </Button>
        )}
        {addToFavorites && <AddToFavorites item={addToFavorites} className="relative top-xxs mr-s hidden md:block" fillColor="fill-black" />}
      </div>
    </section>
  );
}
